/**
 * Transient in-memory store for the multi-page image "scan draft" arranged on
 * the reorder page. Not persisted — a reload clears it (per product decision);
 * nothing uploads until "Generate fingering". Exposed via `useSyncExternalStore`
 * so the upload box and reorder page share one source of truth.
 */
import { useSyncExternalStore } from 'react';

import { makeThumbnailUrl } from '../utils/imageThumbnail';

export interface ScanDraftPage {
  /** Stable id for React keys and drag-reorder identity. */
  id: string;
  /** Original picked file — uploaded as-is (no re-encode). */
  file: File;
  /** Downscaled, display-ready thumbnail URL (data URL, or object URL fallback). */
  thumbnailUrl: string;
}

let pages: ScanDraftPage[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): ScanDraftPage[] {
  return pages;
}

async function pageFromFile(file: File): Promise<ScanDraftPage> {
  const thumbnailUrl = await makeThumbnailUrl(file);
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return { id, file, thumbnailUrl };
}

/** Replace the draft with a fresh set of files (used when seeding from the picker). */
export async function setDraftFromFiles(files: File[]): Promise<void> {
  clearDraft();
  pages = await Promise.all(files.map(pageFromFile));
  emit();
}

/** Append more files to the existing draft (the "＋ add pages" tile). */
export async function addDraftFiles(files: File[]): Promise<void> {
  const added = await Promise.all(files.map(pageFromFile));
  pages = [...pages, ...added];
  emit();
}

/** Remove pages by id (single delete or multi-select delete). */
export function removeDraftPages(ids: ReadonlySet<string>): void {
  for (const page of pages) {
    if (ids.has(page.id)) URL.revokeObjectURL(page.thumbnailUrl);
  }
  pages = pages.filter((page) => !ids.has(page.id));
  emit();
}

/** Move the page at `from` to index `to`, shifting the rest (drag reorder). */
export function reorderDraft(from: number, to: number): void {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= pages.length ||
    to >= pages.length
  ) {
    return;
  }
  const next = [...pages];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  pages = next;
  emit();
}

/** Drop the whole draft and release any object URLs. */
export function clearDraft(): void {
  for (const page of pages) URL.revokeObjectURL(page.thumbnailUrl);
  pages = [];
  emit();
}

/** React hook: subscribe to the current ordered draft pages. */
export function useScanDraft(): ScanDraftPage[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
